import { useState, useEffect, useCallback } from 'react'
import { CLOUD_API, CLOUD_NOT_CONNECTED } from './shared'
import { btn, btnBase, btnSize, cx, hint } from '@ui/classes'
import { credInput, errBanner, okBanner } from '@ui/admin'

import { httpErrorText } from '@core/utils/httpError'
import { db } from '@core/db/db'
import { META_COMPANY_INN, META_STORE_ADDRESS, META_STORE_ID } from '@core/db/schema'
import { Pencil, Trash } from '@ui/icons'
type TaxSystem = 'usn_income' | 'usn_income_outcome' | 'patent' | 'osn'

interface TenantProfile {
  company_name: string
  inn: string | null
  tax_system: TaxSystem
}

interface CloudStore {
  id: string
  name: string
  address: string
  created_at: string
}

const TAX_LABELS: Record<TaxSystem, string> = {
  usn_income: 'УСН Доходы',
  usn_income_outcome: 'УСН Доходы-Расходы',
  patent: 'Патент',
  osn: 'ОСНО'
}

/** Облачная смена из журнала мониторинга касс. */

export function OrgProfileSection({ token }: { token: string }) {
  const useStoreAddress = async (store: CloudStore) => {
    await db.meta.put({ key: META_STORE_ADDRESS, value: store.address })
    await db.meta.put({ key: META_STORE_ID, value: store.id })
    setNote("Адрес и магазин этого POS обновлены")
  }
  const [profile, setProfile] = useState<TenantProfile>({
    company_name: '',
    inn: '',
    tax_system: 'usn_income'
  })
  const [stores, setStores] = useState<CloudStore[]>([])
  const [storeForm, setStoreForm] = useState({ name: '', address: '' })
  // Инлайн-редактирование магазина: id → черновик полей.
  const [storeEdit, setStoreEdit] = useState<{ id: string; name: string; address: string } | null>(
    null
  )
  const [note, setNote] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const reload = useCallback(async () => {
    if (!token) {
      setErr(null)
      return
    }
    try {
      const [pResp, sResp] = await Promise.all([
        fetch(`${CLOUD_API}/tenant/profile`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${CLOUD_API}/tenant/stores`, { headers: { Authorization: `Bearer ${token}` } })
      ])
      if (pResp.ok) {
        const p = (await pResp.json()) as TenantProfile
        setProfile({ company_name: p.company_name ?? '', inn: p.inn ?? '', tax_system: p.tax_system ?? 'usn_income' })
        if (p.inn) await db.meta.put({ key: META_COMPANY_INN, value: p.inn })
      }
      if (sResp.ok) {
        const rows = (await sResp.json()) as CloudStore[]
        setStores(rows)
        const selected = await db.meta.get(META_STORE_ID)
        if (selected && typeof selected.value === 'string') {
          const store = rows.find((item) => item.id === selected.value)
          if (store) await db.meta.put({ key: META_STORE_ADDRESS, value: store.address })
        }
      }
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e))
    }
  }, [token])

  useEffect(() => {
    void reload()
  }, [reload])

  const saveProfile = async () => {
    setErr(null)
    setNote(null)
    if (profile.inn && !/^\d{10}$|^\d{12}$/.test(profile.inn)) {
      setErr('ИНН должен содержать 10 (ООО) или 12 (ИП) цифр')
      return
    }
    setBusy(true)
    try {
      const resp = await fetch(`${CLOUD_API}/tenant/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          company_name: profile.company_name,
          inn: profile.inn || undefined,
          tax_system: profile.tax_system
        })
      })
      if (!resp.ok) {
        const body = (await resp.json().catch(() => ({}))) as { error?: string }
        setErr(body.error || httpErrorText('Сохранение реквизитов', resp.status))
        return
      }
      setNote('Реквизиты организации сохранены')
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const addStore = async (e: React.FormEvent) => {
    e.preventDefault()
    setErr(null)
    if (!storeForm.name.trim() || !storeForm.address.trim()) {
      setErr('Укажите название и адрес магазина')
      return
    }
    setBusy(true)
    try {
      const resp = await fetch(`${CLOUD_API}/tenant/stores`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(storeForm)
      })
      if (!resp.ok) {
        setErr(httpErrorText('Добавление магазина', resp.status))
        return
      }
      setStoreForm({ name: '', address: '' })
      setNote('Магазин добавлен')
      await reload()
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const saveStoreEdit = async () => {
    if (!storeEdit) return
    setErr(null)
    if (!storeEdit.name.trim() || !storeEdit.address.trim()) {
      setErr('Укажите название и адрес магазина')
      return
    }
    setBusy(true)
    try {
      const resp = await fetch(`${CLOUD_API}/tenant/stores/${storeEdit.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: storeEdit.name, address: storeEdit.address })
      })
      if (!resp.ok) {
        setErr(httpErrorText('Сохранение магазина', resp.status))
        return
      }
      setStoreEdit(null)
      setNote('Магазин обновлён')
      await reload()
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const deleteStore = async (id: string) => {
    if (!window.confirm('Удалить магазин?')) return
    setErr(null)
    setBusy(true)
    try {
      const resp = await fetch(`${CLOUD_API}/tenant/stores/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!resp.ok) {
        setErr(httpErrorText('Удаление магазина', resp.status))
        return
      }
      setNote('Магазин удалён')
      await reload()
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      {note && <p className={okBanner}>{note}</p>}
      {err && <p className={errBanner}>{err}</p>}
      {!token && (
        <p className={hint}>{CLOUD_NOT_CONNECTED}. Реквизиты и точки доступны после входа.</p>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-small">
          Название юрлица (ИП/ООО)
          <input
            value={profile.company_name}
            onChange={(e) => setProfile((p) => ({ ...p, company_name: e.target.value }))}
            className={cx(credInput, 'min-w-[200px]')}
          />
        </label>
        <label className="flex flex-col gap-1 text-small">
          ИНН (10 или 12 цифр)
          <input
            value={profile.inn ?? ''}
            onChange={(e) => setProfile((p) => ({ ...p, inn: e.target.value }))}
            className={credInput}
            inputMode="numeric"
            maxLength={12}
          />
        </label>
        <label className="flex flex-col gap-1 text-small">
          Система налогообложения
          <select
            value={profile.tax_system}
            onChange={(e) => setProfile((p) => ({ ...p, tax_system: e.target.value as TaxSystem }))}
           className={credInput}>
            {(Object.keys(TAX_LABELS) as TaxSystem[]).map((k) => (
              <option key={k} value={k}>
                {TAX_LABELS[k]}
              </option>
            ))}
          </select>
        </label>
        <button onClick={() => void saveProfile()} disabled={busy} className={cx(btnBase, btn.secondary, btnSize.sm)}>
          Сохранить реквизиты
        </button>
      </div>

      <div className="mt-4">
        <strong>Торговые точки:</strong>
        {stores.length === 0 ? (
          <p className={hint}>Магазины ещё не добавлены.</p>
        ) : (
          <ul className="my-2 list-none p-0">
            {stores.map((s) => (
              <li
                key={s.id}
                className="mb-1.5 flex flex-wrap items-center gap-2 text-sm"
              >
                {storeEdit?.id === s.id ? (
                  <>
                    <input
                      value={storeEdit.name}
                      onChange={(e) =>
                        setStoreEdit((v) => (v ? { ...v, name: e.target.value } : v))
                      }
                      placeholder="Название"
                      className={credInput}
                    />
                    <input
                      value={storeEdit.address}
                      onChange={(e) =>
                        setStoreEdit((v) => (v ? { ...v, address: e.target.value } : v))
                      }
                      placeholder="Адрес"
                      className={cx(credInput, 'min-w-[220px]')}
                    />
                    <button onClick={() => void saveStoreEdit()} disabled={busy} className={cx(btnBase, btn.secondary, btnSize.sm)}>
                      Сохранить
                    </button>
                    <button onClick={() => setStoreEdit(null)} disabled={busy} className={cx(btnBase, btn.secondary, btnSize.sm)}>
                      Отмена
                    </button>
                  </>
                ) : (
                  <>
                    <span className="min-w-[160px] flex-1">
                      <strong>{s.name}</strong> — {s.address}
                    </span>
                    <button
                      onClick={() => void useStoreAddress(s)}
                      className={cx(btnBase, btn.secondary, btnSize.sm)}
                    >
                      Адрес POS
                    </button>
                    <button
                      onClick={() => setStoreEdit({ id: s.id, name: s.name, address: s.address })}
                     className={cx(btnBase, btn.secondary, btnSize.sm)}>
                      <Pencil size={16} /> Редактировать
                    </button>
                    <button
                      className={cx(btnBase, btn.dangerGhost, btnSize.sm)}
                      onClick={() => void deleteStore(s.id)}
                      disabled={busy}
                    >
                      <Trash size={16} /> Удалить
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
        <form onSubmit={addStore} className="mt-2 flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 text-small">
            Название филиала
            <input value={storeForm.name} onChange={(e) => setStoreForm((f) => ({ ...f, name: e.target.value }))} className={credInput} />
          </label>
          <label className="flex flex-col gap-1 text-small">
            Адрес расчётов
            <input value={storeForm.address} onChange={(e) => setStoreForm((f) => ({ ...f, address: e.target.value }))} className={cx(credInput, 'min-w-[240px]')} />
          </label>
          <button type="submit" disabled={busy} className={cx(btnBase, btn.secondary, btnSize.sm)}>
            + Добавить магазин
          </button>
        </form>
      </div>
    </div>
  )
}

/** Сотрудник (из облачного /auth/users). */
